import { useRef, useState, useEffect } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'

import { SearchInput } from 'libs/searchspring/components/quick-search/SearchInput'
import { QuickSearchDropdown } from 'libs/searchspring/components/quick-search/QuickSearchDropdown'
import { SearchspringAutocompleteProvider } from 'libs/searchspring/fetchers/autocomplete'

function QuickSearch({ id = 'quick-search' }) {
  const searchInputRef = useRef()
  const [inputValue, setInputValue] = useState()
  const [query, setQuery] = useState()
  const closeButtonRef = useRef()
  const modalRef = useRef()
  const [redirect, setRedirect] = useState(false)

  const handleClose = (event) => {
    event.preventDefault()
    modalRef.current?.handleHide()
  }

  const handleUpdateValue = (event) => {
    setInputValue(event.target.value)
  }

  const resetInputValue = () => {
    setInputValue('')
    searchInputRef.current.value = ''
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(inputValue)
    }, 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [inputValue])

  useEffect(() => {
    if (!modalRef.current || !searchInputRef.current) return

    const modal = modalRef.current
    const input = searchInputRef.current

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ui') {
          if (modal.dataset.ui === 'visible') {
            setTimeout(() => {
              input?.focus()
            }, 100)
          }
        }
      })
    })

    observer.observe(modal, {
      attributes: true,
      attributeFilter: ['data-ui']
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <>
      {/* Toggle button - always visible, outside the modal */}
      <button
        className="flex items-center justify-center size-[40px] sm-plus:size-[44px] link-fade"
        data-modal-parent={id}
        data-modal-role="toggle"
        aria-label="Toggle search"
      >
        <div className="size-[17px] children:size-full">
          <Icon id="magnifying-glass" />
        </div>
      </button>

      <x-modal className="group/search grow flex items-center h-full" data-id={id} data-body-lock ref={modalRef}>
        <div className="w-full flex items-center relative">
          <form action="/search" method="get" className="size-full max-w-[0px] overflow-hidden group-data-visible/search:max-w-[100vw] opacity-0 group-data-visible/search:opacity-100 invisible group-data-visible/search:visible pointer-events-none group-data-visible/search:pointer-events-auto z-2">
            <div className="flex items-center justify-between gap-x-1.5 size-full bg-brand-white">
              <SearchInput ref={searchInputRef} redirect={redirect} onInput={handleUpdateValue} />
              <div className={'flex items-center justify-end gap-x-1.5'}>
                {inputValue ? (
                  <button className="text-s text-line link-fade" onClick={resetInputValue} type="reset" aria-label="Clear search">
                    Clear
                  </button>
                ) : null}
                <button
                  className="flex items-center justify-center size-[40px] sm-plus:size-[44px] link-fade"
                  data-modal-parent={id}
                  data-modal-role="close"
                  ref={closeButtonRef}
                  onClick={handleClose}
                  aria-label="Close modal"
                >
                  <div class="size-[14px] children:size-full">
                    <Icon id="close" />
                  </div>
                </button>
              </div>
            </div>
          </form>
        </div>
        <div className="absolute top-header left-0 w-full h-fit [.layout-floating_&]:px-margin [.layout-floating_&]:pt-0.5 opacity-0 invisible pointer-events-none group-data-visible/search:opacity-100 group-data-visible/search:visible group-data-visible/search:pointer-events-auto transition-all">
          <SearchspringAutocompleteProvider runOnMount={false} resultsPerPage={4}>
            <QuickSearchDropdown query={query} redirect={redirect} setRedirect={setRedirect} />
          </SearchspringAutocompleteProvider>
        </div>
        <button
          className="fixed [.layout-floating_&]:top-0 [.layout-solid_&]:top-header left-0 w-full [.layout-floating_&]:h-view [.layout-solid_&]:h-view bg-neutral-black/60 backdrop-blur-sm opacity-0 group-data-visible/search:opacity-100 invisible group-data-visible/search:visible cursor-w-resize -z-1 transition-all"
          data-modal-parent={id}
          data-modal-role="close"
          aria-label="Close modal"
        ></button>
      </x-modal>
    </>
  )
}

export function WrappedQuickSearch({ id }) {
  return (
    <SearchspringAutocompleteProvider runOnMount={false}>
      <QuickSearch id={id} />
    </SearchspringAutocompleteProvider>
  )
}
